import { useEffect, useState } from 'react';

/**
 * Full-screen working mode for a list table.
 *
 * The table is lifted out of the page into a fixed panel that owns the whole
 * viewport, so every pixel of height goes to rows. The component stays mounted
 * across the switch — it is the same React state driving both views — so
 * search, filters, sort, grouping, column choice and page all survive entering
 * and leaving.
 *
 * While the panel is open the page behind it must not scroll, and Escape
 * closes it — unless a column popover is open, in which case Escape belongs to
 * that popover and the panel stays put.
 *
 * @returns {[boolean, Function]} state and setter
 */
const OPEN_POPOVER = '.dg-menu, .cl-th-menu';

export default function useFullscreenPanel() {
  const [fullscreen, setFullscreen] = useState(false);

  useEffect(() => {
    if (!fullscreen) return undefined;

    const onKey = (e) => {
      if (e.key !== 'Escape') return;
      // A popover eats the first Escape; the panel closes on the next one.
      if (document.querySelector(OPEN_POPOVER)) return;
      setFullscreen(false);
    };

    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    document.addEventListener('keydown', onKey);

    return () => {
      document.removeEventListener('keydown', onKey);
      document.body.style.overflow = previousOverflow;
    };
  }, [fullscreen]);

  return [fullscreen, setFullscreen];
}
